// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

import { getRemoteAccessToken } from '@web/lib/remoteAuth';

const REMOTE_TOKEN_HEADER = 'X-Remote-Control-Token';
const PENDING_COMMAND_REQUESTS_KEY =
  'eigent:remote-control-command-requests:v1';
const PENDING_COMMAND_REQUEST_TTL_MS = 24 * 60 * 60 * 1000;
const PENDING_COMMAND_REQUEST_LIMIT = 32;

type PendingCommandRequest = {
  fingerprint: string;
  requestId: string;
  createdAt: number;
};

let pendingCommandRequestsFallback: PendingCommandRequest[] = [];

function stableValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stableValue);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, item]) => [key, stableValue(item)])
    );
  }
  return value;
}

function readPendingCommandRequests(): PendingCommandRequest[] {
  const now = Date.now();
  try {
    const parsed = JSON.parse(
      window.sessionStorage.getItem(PENDING_COMMAND_REQUESTS_KEY) || '[]'
    );
    pendingCommandRequestsFallback = Array.isArray(parsed)
      ? parsed
          .filter(
            (item): item is PendingCommandRequest =>
              typeof item?.fingerprint === 'string' &&
              typeof item?.requestId === 'string' &&
              typeof item?.createdAt === 'number' &&
              now - item.createdAt <= PENDING_COMMAND_REQUEST_TTL_MS
          )
          .slice(-PENDING_COMMAND_REQUEST_LIMIT)
      : [];
  } catch {
    pendingCommandRequestsFallback = pendingCommandRequestsFallback
      .filter((item) => now - item.createdAt <= PENDING_COMMAND_REQUEST_TTL_MS)
      .slice(-PENDING_COMMAND_REQUEST_LIMIT);
  }
  return pendingCommandRequestsFallback;
}

function writePendingCommandRequests(items: PendingCommandRequest[]): void {
  pendingCommandRequestsFallback = items.slice(-PENDING_COMMAND_REQUEST_LIMIT);
  try {
    window.sessionStorage.setItem(
      PENDING_COMMAND_REQUESTS_KEY,
      JSON.stringify(pendingCommandRequestsFallback)
    );
  } catch {
    // The in-memory fallback still preserves identity within this page load.
  }
}

function createClientRequestId(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);
  return Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join(
    ''
  );
}

function commandRequestIdentity(
  sessionId: string,
  body: Record<string, unknown>
): { fingerprint: string; requestId: string } {
  const fingerprint = JSON.stringify(
    stableValue({ session_id: sessionId, ...body })
  );
  const existing = readPendingCommandRequests().find(
    (item) => item.fingerprint === fingerprint
  );
  if (existing) {
    return { fingerprint, requestId: existing.requestId };
  }
  const requestId = createClientRequestId();
  writePendingCommandRequests([
    ...pendingCommandRequestsFallback,
    { fingerprint, requestId, createdAt: Date.now() },
  ]);
  return { fingerprint, requestId };
}

function clearCommandRequestIdentity(fingerprint: string): void {
  writePendingCommandRequests(
    readPendingCommandRequests().filter(
      (item) => item.fingerprint !== fingerprint
    )
  );
}

export class RemoteControlError extends Error {
  readonly status: number;
  readonly code: string | null;
  readonly payload: unknown;
  constructor(
    status: number,
    message: string,
    code: string | null,
    payload: unknown
  ) {
    super(message);
    this.name = 'RemoteControlError';
    this.status = status;
    this.code = code;
    this.payload = payload;
  }
}

function extractErrorCode(payload: unknown): string | null {
  if (!payload || typeof payload !== 'object') {
    return null;
  }
  const record = payload as Record<string, any>;
  const detail = record.detail;
  if (detail && typeof detail === 'object' && typeof detail.code === 'string') {
    return detail.code;
  }
  if (typeof record.code === 'string') {
    return record.code;
  }
  return null;
}

export type RemoteControlSession = {
  session_id: string;
  desktop_instance_id: string;
  space_id?: string | null;
  space_name?: string | null;
  project_id?: string | null;
  active_task_id?: string | null;
  brain_session_id?: string | null;
  current_project_id?: string | null;
  current_task_id?: string | null;
  current_history_id?: string | null;
  current_brain_session_id?: string | null;
  title: string;
  status: string;
  bridge_status: 'online' | 'offline' | string;
  execution_mode: string;
  capabilities: Record<string, unknown>;
  created_at: string | null;
  expires_at: string;
};

export type RemoteControlSpace = {
  id: string;
  user_id: string;
  name: string;
  description?: string | null;
  source_type: 'blank' | 'folder' | 'legacy' | string;
  root_path?: string | null;
  status: string;
  schema_version: number;
  metadata?: Record<string, unknown> | null;
};

export type RemoteControlProject = {
  id: string;
  user_id: string;
  space_id: string;
  name: string;
  description?: string | null;
  mode?: 'single-agent' | 'workforce' | string | null;
  status: string;
  workdir_mode?: string | null;
  metadata?: Record<string, unknown> | null;
  created_at?: string | null;
  updated_at?: string | null;
};

export type RemoteControlOverlay = {
  id: number;
  space_id: string;
  project_id: string;
  run_id: string;
  path: string;
  status: string;
  hash?: string | null;
  base_hash?: string | null;
  metadata?: Record<string, unknown> | null;
};

export type RemoteControlTarget = {
  project_id: string | null;
  task_id: string | null;
  brain_session_id: string | null;
};

export type ReadyRemoteControlTarget = {
  project_id: string;
  task_id: string | null;
  brain_session_id: string;
};

export type RemoteControlStep = {
  step_id: number | string;
  task_id: string;
  project_id?: string | null;
  step: string;
  data: unknown;
  timestamp?: number | null;
};

export type RemoteCanonicalEvent = {
  event_id: string;
  project_id: string;
  run_id: string;
  run_sequence: number;
  run_version: number;
  cloud_cursor: number;
  event_type: string;
  payload: Record<string, unknown>;
  legacy_step: string | null;
  created_at: string;
  ingested_at: string;
};

/** JSON-safe values accepted by the versioned work-item event contract. */
export type RemoteJsonValue =
  | null
  | boolean
  | number
  | string
  | RemoteJsonValue[]
  | { [key: string]: RemoteJsonValue };

export type RemoteWorkItemTypeV1 =
  | 'reasoning_summary'
  | 'tool_call'
  | 'agent_message'
  | 'agent_call'
  | 'human_input'
  | 'plan'
  | 'notice';

export type RemoteWorkItemLifecycleV1 =
  | 'started'
  | 'delta'
  | 'completed'
  | 'failed'
  | 'interrupted';

export type RemoteWorkItemStatusV1 =
  | 'queued'
  | 'in_progress'
  | 'waiting'
  | 'completed'
  | 'failed'
  | 'interrupted';

export type RemoteAgentRoleV1 =
  | 'primary'
  | 'coordinator'
  | 'worker'
  | 'subagent';

/**
 * Correlation and lifecycle fields shared by every v1 work-item event.
 * Delivery identity and ordering remain on the enclosing canonical event.
 */
export type RemoteWorkItemEventContextV1 = {
  schema_version: 1;
  /** Stable retry/attempt discriminator; unlike envelope run_version. */
  attempt_version?: number;
  item_id: string;
  item_type: RemoteWorkItemTypeV1;
  lifecycle: RemoteWorkItemLifecycleV1;
  status: RemoteWorkItemStatusV1;
  agent_execution_id: string;
  parent_agent_execution_id: string | null;
  agent_role: RemoteAgentRoleV1;
  agent_name?: string;
  tool_call_id: string | null;
  exchange_id: string | null;
  human_interaction_id: string | null;
  parallel_group_id: string | null;
  started_at: string | null;
  completed_at: string | null;
  part_index: number | null;
};

export type RemoteWorkItemEventDataV1 =
  | {
      item_type: 'reasoning_summary';
      summary_text?: string;
      text_delta?: string;
      summary_part_index?: number;
    }
  | {
      item_type: 'tool_call';
      tool_name: string;
      input?: RemoteJsonValue;
      output?: string;
      output_delta?: string;
      result?: RemoteJsonValue;
      error?: { code?: string; message: string };
      exit_code?: number;
      cwd?: string;
      duration_ms?: number;
    }
  | {
      item_type: 'agent_message';
      phase: 'commentary' | 'final_answer';
      text?: string;
      text_delta?: string;
    }
  | {
      item_type: 'agent_call';
      child_agent_execution_id: string;
      prompt?: string;
      result_summary?: string;
    }
  | {
      item_type: 'human_input';
      question: string;
      response?: RemoteJsonValue;
    }
  | {
      item_type: 'plan' | 'notice';
      text?: string;
      text_delta?: string;
    };

export type RemoteWorkItemEventPayloadV1 = RemoteWorkItemEventContextV1 &
  RemoteWorkItemEventDataV1;

export type RemoteProjectSnapshot = {
  project_id: string;
  current_cursor: number;
  runs: Array<{
    run_id: string;
    status: string;
    expected_next_run_sequence: number;
    updated_at: string;
  }>;
  recent_events: RemoteCanonicalEvent[];
  artifact_events?: RemoteCanonicalEvent[];
  events_truncated: boolean;
};

export type RemoteCommandProjection = {
  client_request_id?: string | null;
  human_interaction_id?: string | null;
  receipt_state: string;
  admission_state: string;
  execution_state: string;
  actual_execution_state?: string | null;
  late_result?: boolean;
  integrity_alert?: string | null;
};

export type RemoteCommandState = RemoteCommandProjection & {
  command_id: string;
  project_id: string;
  run_id?: string | null;
  client_request_id?: string | null;
  human_interaction_id?: string | null;
  route_device_id: string;
  route_version: number;
  expires_at: string;
  receipt_grace_until: string;
  events: Array<{
    event_id: string;
    producer: string;
    desktop_event_sequence?: number | null;
    cloud_recorded_version?: number | null;
    event_type: string;
    payload: Record<string, unknown>;
    occurred_at: string;
  }>;
};

export type RemoteControlCommandResponse = {
  command_id: string;
  status: string;
  client_request_id?: string | null;
  project_id?: string | null;
  run_id?: string | null;
  human_interaction_id?: string | null;
  next_task_id?: string | null;
};

export type RemoteControlAuthExchangeResponse = {
  remote_access_token: string;
  token_type: 'bearer' | string;
  expires_in: number;
  expires_at: string;
  user: {
    id: number;
    email?: string | null;
    username?: string | null;
  };
};

type RemoteFetchOptions = {
  method?: string;
  token: string;
  accessSessionId?: string;
  body?: unknown;
  params?: Record<string, string | number | boolean | null | undefined>;
};

function stripTrailingSlash(value: string): string {
  return value.replace(/\/$/, '');
}

function isHttpUrl(value: unknown): value is string {
  return typeof value === 'string' && /^https?:\/\//.test(value);
}

function shouldUseAbsoluteApiOrigin(): boolean {
  return import.meta.env.VITE_USE_LOCAL_PROXY === 'false';
}

function apiOriginForHttp(): string {
  const proxyUrl = import.meta.env.VITE_PROXY_URL;
  if (shouldUseAbsoluteApiOrigin() && isHttpUrl(proxyUrl)) {
    return stripTrailingSlash(proxyUrl);
  }
  return window.location.origin;
}

function apiOriginForWebSocket(): string {
  const proxyUrl = import.meta.env.VITE_PROXY_URL;
  if (shouldUseAbsoluteApiOrigin() && isHttpUrl(proxyUrl)) {
    return stripTrailingSlash(proxyUrl);
  }
  return window.location.origin;
}

function sessionIdFromPath(path: string): string | null {
  const match = path.match(/\/remote-control\/sessions\/([^/]+)/);
  if (!match?.[1]) {
    return null;
  }
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

function buildApiUrl(
  path: string,
  params?: RemoteFetchOptions['params']
): string {
  const url = new URL(path, apiOriginForHttp());
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, String(value));
    }
  });
  return url.toString();
}

function describeRemoteError(payload: unknown, fallback: string): string {
  if (payload && typeof payload === 'object') {
    const record = payload as Record<string, any>;
    const detail = record.detail;
    if (typeof detail === 'string') {
      return detail;
    }
    if (detail && typeof detail === 'object') {
      return String(detail.message || detail.code || JSON.stringify(detail));
    }
    return String(record.message || record.error || fallback);
  }
  if (typeof payload === 'string' && payload.trim()) {
    return payload;
  }
  return fallback;
}

async function remoteFetch<T>(
  path: string,
  { method = 'GET', token, accessSessionId, body, params }: RemoteFetchOptions
): Promise<T> {
  const remoteSessionId = accessSessionId || sessionIdFromPath(path);
  const remoteAccessToken = remoteSessionId
    ? getRemoteAccessToken(remoteSessionId)
    : '';
  const headers: HeadersInit = {
    Accept: 'application/json',
    ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
    [REMOTE_TOKEN_HEADER]: token,
    ...(remoteAccessToken
      ? { Authorization: `Bearer ${remoteAccessToken}` }
      : {}),
  };

  const response = await fetch(buildApiUrl(path, params), {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (response.status === 204) {
    return undefined as T;
  }
  const contentType = response.headers.get('content-type') || '';
  const payload = contentType.includes('application/json')
    ? await response.json().catch(() => null)
    : await response.text().catch(() => '');
  if (!response.ok) {
    throw new RemoteControlError(
      response.status,
      describeRemoteError(payload, `HTTP ${response.status}`),
      extractErrorCode(payload),
      payload
    );
  }
  return payload as T;
}

export type WorkspaceBundleSummary = {
  id: string;
  workspace_id: string;
  owner_id: string;
  publisher_type: 'user' | 'organization';
  publisher_id: string;
  publisher_namespace: string;
  slug: string;
  package_name: string;
  name: string;
  visibility: 'private' | 'team' | 'public' | string;
  latest_published_revision_id?: string | null;
  source_revision_id?: string | null;
  created_at: string;
  updated_at: string;
};

export type WorkspaceBundleRevision = {
  id: string;
  bundle_id: string;
  revision: number;
  manifest: Record<string, any>;
  manifest_digest: string;
  status: string;
  signed_digest?: string | null;
  assets: Array<{
    id: string;
    logical_path: string;
    content_digest: string;
    media_type: string;
    size_bytes: number;
    provenance: string;
  }>;
};

export type SpaceBundleInstallation = {
  id: string;
  space_id: string;
  bundle_id: string;
  installed_revision_id: string;
  state: string;
  version: number;
  last_materialized_at?: string | null;
  confirmed_manifest_digest?: string | null;
  confirmed_at?: string | null;
};

export type SpaceEnvironment = {
  protocol_capabilities?: string[];
  installation?: SpaceBundleInstallation | null;
  revision?: WorkspaceBundleRevision | null;
  bindings: Record<string, { connector_id: string; bound: string }>;
  environment_projection?: Record<string, any> | null;
};

export type CloudMemoryScopeType = 'project' | 'space' | 'user';

export type CloudMemoryEntry = {
  memory_id: string;
  scope_type: CloudMemoryScopeType;
  scope_id: string;
  kind: string;
  content: string | null;
  content_available: boolean;
  priority: string;
  version: number;
  token_count: number;
  pinned_by_user: boolean;
  confirmed_by_user: boolean;
  created_by: string;
  source_trust: string;
  sensitivity: string;
  source_refs: string[];
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
};

export type CloudMemoryScope = {
  scope_type: CloudMemoryScopeType;
  scope_id: string;
  owner_kind: string;
  revision: number;
  capture_enabled: boolean;
  use_enabled: boolean;
  sync_scope: 'full_memory';
  token_limit: number;
  current_token_count: number;
  processed_through_watermark: string | null;
  watermark_kind: string | null;
  freshness: 'current' | 'delayed' | 'offline_unknown';
  entries: CloudMemoryEntry[];
};

export function getCloudMemoryScope(
  sessionId: string,
  token: string,
  scopeType: CloudMemoryScopeType,
  scopeId?: string
): Promise<CloudMemoryScope> {
  const path = `/api/v1/remote-control/sessions/${encodeURIComponent(sessionId)}/memory/${scopeType}`;
  return remoteFetch(path, {
    token,
    accessSessionId: sessionId,
    params: scopeType === 'user' ? undefined : { scope_id: scopeId || '' },
  });
}

export async function listWorkspaceBundles(
  sessionId: string,
  token: string
): Promise<WorkspaceBundleSummary[]> {
  const response = await remoteFetch<{ items: WorkspaceBundleSummary[] }>(
    '/api/v1/workspace-bundles',
    { token, accessSessionId: sessionId }
  );
  return response.items;
}

export function getWorkspaceBundleRevision(
  sessionId: string,
  token: string,
  bundleId: string,
  revisionId: string
): Promise<WorkspaceBundleRevision> {
  return remoteFetch(
    `/api/v1/workspace-bundles/${encodeURIComponent(bundleId)}/revisions/${encodeURIComponent(revisionId)}`,
    { token, accessSessionId: sessionId }
  );
}

export function getSpaceEnvironment(
  sessionId: string,
  token: string,
  spaceId: string
): Promise<SpaceEnvironment> {
  return remoteFetch(
    `/api/v1/spaces/${encodeURIComponent(spaceId)}/environment`,
    {
      token,
      accessSessionId: sessionId,
    }
  );
}

export function installSpaceBundle(
  sessionId: string,
  token: string,
  spaceId: string,
  bundleId: string,
  revisionId: string
): Promise<SpaceBundleInstallation> {
  return remoteFetch(
    `/api/v1/spaces/${encodeURIComponent(spaceId)}/bundle:install`,
    {
      method: 'POST',
      token,
      accessSessionId: sessionId,
      body: { bundle_id: bundleId, revision_id: revisionId },
    }
  );
}

export function confirmSpaceBundleInstall(
  sessionId: string,
  token: string,
  spaceId: string,
  installation: SpaceBundleInstallation,
  revision: WorkspaceBundleRevision
): Promise<SpaceBundleInstallation> {
  const spec =
    revision.manifest &&
    typeof revision.manifest === 'object' &&
    revision.manifest.spec &&
    typeof revision.manifest.spec === 'object'
      ? (revision.manifest.spec as Record<string, unknown>)
      : {};
  const connectors = Array.isArray(spec.connectors) ? spec.connectors : [];
  const reviewedSlots = connectors.flatMap((value) => {
    const item =
      value && typeof value === 'object'
        ? (value as Record<string, unknown>)
        : {};
    const slotId = String(item.connectionSlot || '');
    const connectorId = String(item.connector || '');
    return slotId && connectorId
      ? [
          {
            slot_id: slotId,
            connector_id: connectorId,
            required_grants: Array.isArray(item.requiredGrants)
              ? item.requiredGrants.map(String).sort()
              : [],
          },
        ]
      : [];
  });
  return remoteFetch(
    `/api/v1/spaces/${encodeURIComponent(spaceId)}/bundle:confirm-install`,
    {
      method: 'POST',
      token,
      accessSessionId: sessionId,
      body: {
        bundle_id: revision.bundle_id,
        revision_id: revision.id,
        expected_version: installation.version,
        reviewed_manifest_digest: revision.manifest_digest,
        reviewed_slots: reviewedSlots,
      },
    }
  );
}

export function upgradeSpaceBundle(
  sessionId: string,
  token: string,
  spaceId: string,
  revisionId: string,
  current: SpaceBundleInstallation
): Promise<SpaceBundleInstallation> {
  return remoteFetch(
    `/api/v1/spaces/${encodeURIComponent(spaceId)}/bundle:upgrade`,
    {
      method: 'POST',
      token,
      accessSessionId: sessionId,
      body: {
        revision_id: revisionId,
        expected_installed_revision_id: current.installed_revision_id,
        expected_version: current.version,
      },
    }
  );
}

export function getRemoteControlTarget(
  session: RemoteControlSession
): RemoteControlTarget {
  if (session.current_project_id) {
    const isLegacyProject = session.current_project_id === session.project_id;
    return {
      project_id: session.current_project_id,
      task_id: session.current_task_id ?? null,
      brain_session_id:
        session.current_brain_session_id ||
        (isLegacyProject ? session.brain_session_id : null) ||
        null,
    };
  }
  return {
    project_id: session.project_id || null,
    task_id: session.active_task_id || null,
    brain_session_id: session.brain_session_id || null,
  };
}

export function isRemoteControlTargetReady(
  target: RemoteControlTarget | null
): target is ReadyRemoteControlTarget {
  return Boolean(target?.project_id && target?.brain_session_id);
}

export function getRemoteControlEventSocketUrl(sessionId: string): string {
  const url = new URL(
    `/api/v1/remote-control/sessions/${encodeURIComponent(
      sessionId
    )}/events/subscribe`,
    apiOriginForWebSocket()
  );
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return url.toString();
}

export function exchangeRemoteControlAuth(
  sessionId: string,
  token: string,
  rcCode: string
): Promise<RemoteControlAuthExchangeResponse> {
  return remoteFetch('/api/v1/remote-control/auth/exchange', {
    token,
    method: 'POST',
    body: {
      session_id: sessionId,
      rc_code: rcCode,
    },
  });
}

export function getRemoteControlSession(
  sessionId: string,
  token: string
): Promise<RemoteControlSession> {
  return remoteFetch(`/api/v1/remote-control/sessions/${sessionId}`, {
    token,
  });
}

export function listRemoteControlProjects(
  sessionId: string,
  token: string
): Promise<{ space: RemoteControlSpace; items: RemoteControlProject[] }> {
  return remoteFetch(`/api/v1/remote-control/sessions/${sessionId}/projects`, {
    token,
  });
}

export function createRemoteControlProject(
  sessionId: string,
  token: string,
  input: {
    name: string;
    description?: string;
    mode?: 'single-agent' | 'workforce';
  }
): Promise<RemoteControlProject> {
  return remoteFetch(`/api/v1/remote-control/sessions/${sessionId}/projects`, {
    token,
    method: 'POST',
    body: {
      mode: 'single-agent',
      ...input,
    },
  });
}

export function listRemoteControlSteps(
  sessionId: string,
  token: string,
  projectId: string | null,
  since = 0,
  limit = 200
): Promise<{
  items: RemoteControlStep[];
  has_more: boolean;
  next_since: number;
}> {
  return remoteFetch(`/api/v1/remote-control/sessions/${sessionId}/steps`, {
    token,
    params: {
      ...(projectId ? { project_id: projectId } : {}),
      since,
      limit,
      order: 'asc',
    },
  });
}

export function getRemoteControlSnapshot(
  sessionId: string,
  token: string,
  projectId?: string | null,
  eventLimit = 1000
): Promise<RemoteProjectSnapshot> {
  return remoteFetch(`/api/v1/remote-control/sessions/${sessionId}/snapshot`, {
    token,
    params: {
      ...(projectId ? { project_id: projectId } : {}),
      event_limit: eventLimit,
    },
  });
}

export function listRemoteControlEvents(
  sessionId: string,
  token: string,
  projectId: string,
  afterCursor: number,
  limit = 1000
): Promise<{
  project_id: string;
  current_cursor: number;
  next_cursor: number;
  has_more: boolean;
  items: RemoteCanonicalEvent[];
}> {
  return remoteFetch(`/api/v1/remote-control/sessions/${sessionId}/events`, {
    token,
    params: {
      project_id: projectId,
      after_cursor: afterCursor,
      limit,
    },
  });
}

export function getRemoteControlCommandState(
  sessionId: string,
  token: string,
  commandId: string
): Promise<RemoteCommandState> {
  return remoteFetch(
    `/api/v1/remote-control/sessions/${sessionId}/commands/${encodeURIComponent(
      commandId
    )}`,
    { token }
  );
}

export function sendRemoteControlCommand(
  sessionId: string,
  token: string,
  type: string,
  payload: Record<string, unknown>,
  target?: RemoteControlTarget | null
): Promise<RemoteControlCommandResponse> {
  const commandBody = {
    source_channel: 'remote_web',
    type,
    payload,
    target_project_id: target?.project_id,
    target_task_id: target?.task_id,
    target_brain_session_id: target?.brain_session_id,
  };
  const identity = commandRequestIdentity(sessionId, commandBody);
  return remoteFetch<RemoteControlCommandResponse>(
    `/api/v1/remote-control/sessions/${sessionId}/commands`,
    {
      token,
      method: 'POST',
      body: {
        ...commandBody,
        client_request_id: identity.requestId,
      },
    }
  ).then((response) => {
    // Interaction decisions must keep the exact request identity after an
    // HTTP success: the Desktop durable result may still be delayed or the
    // response may be reconciled from another tab. Retrying therefore sends
    // the identical command rather than creating a second decision.
    if (type !== 'interaction_decision') {
      clearCommandRequestIdentity(identity.fingerprint);
    }
    return {
      ...response,
      // Older servers do not echo the idempotency key yet. Preserve the
      // locally generated identity so optimistic UI state can still reconcile
      // with a later canonical command/run projection.
      client_request_id: response.client_request_id ?? identity.requestId,
    };
  });
}

export function getRemoteArtifactDownload(
  sessionId: string,
  token: string,
  chatFileId: number
): Promise<{ download_url: string; content_digest?: string | null }> {
  return remoteFetch(
    `/api/v1/remote-control/sessions/${sessionId}/files/${chatFileId}/download`,
    { token, accessSessionId: sessionId }
  );
}

export const __remoteControlTestHooks = {
  resetPendingCommandRequests() {
    pendingCommandRequestsFallback = [];
    try {
      window.sessionStorage.removeItem(PENDING_COMMAND_REQUESTS_KEY);
    } catch {
      // no-op
    }
  },
};

export function patchRemoteControlTarget(
  sessionId: string,
  token: string,
  input: {
    project_id: string;
    task_id?: string | null;
    history_id?: string | null;
  }
): Promise<{
  space_id?: string | null;
  current_project_id: string;
  current_task_id?: string | null;
  current_history_id?: string | null;
  current_brain_session_id: string;
  desktop_ready: 'pending' | 'ready' | 'failed';
}> {
  return remoteFetch(`/api/v1/remote-control/sessions/${sessionId}/target`, {
    token,
    method: 'PATCH',
    body: input,
  });
}

export function listRemoteControlOverlays(
  sessionId: string,
  token: string,
  projectId: string,
  runId?: string | null
): Promise<{
  space_id: string;
  project_id: string;
  overlays: RemoteControlOverlay[];
}> {
  return remoteFetch(
    `/api/v1/remote-control/sessions/${sessionId}/projects/${projectId}/overlays`,
    {
      token,
      params: runId ? { run_id: runId } : undefined,
    }
  );
}

export function applyRemoteControlProjectRun(
  sessionId: string,
  token: string,
  projectId: string,
  input: { run_id: string; paths?: string[] | null; confirm: boolean }
): Promise<RemoteControlCommandResponse> {
  return remoteFetch(
    `/api/v1/remote-control/sessions/${sessionId}/projects/${projectId}/apply`,
    {
      token,
      method: 'POST',
      body: input,
    }
  );
}

export function discardRemoteControlProjectOverlays(
  sessionId: string,
  token: string,
  projectId: string,
  input: { run_id?: string | null; paths?: string[] | null; confirm: boolean }
): Promise<RemoteControlCommandResponse> {
  return remoteFetch(
    `/api/v1/remote-control/sessions/${sessionId}/projects/${projectId}/discard`,
    {
      token,
      method: 'POST',
      body: input,
    }
  );
}

export function refreshRemoteControlProject(
  sessionId: string,
  token: string,
  projectId: string,
  input: { force?: boolean } = {}
): Promise<RemoteControlCommandResponse> {
  return remoteFetch(
    `/api/v1/remote-control/sessions/${sessionId}/projects/${projectId}/refresh`,
    {
      token,
      method: 'POST',
      body: input,
    }
  );
}

export function extendRemoteControlSession(
  sessionId: string,
  token: string,
  extendSeconds: number
): Promise<{ expires_at: string }> {
  return remoteFetch(`/api/v1/remote-control/sessions/${sessionId}/extend`, {
    token,
    method: 'POST',
    body: { extend_seconds: extendSeconds },
  });
}

export function revokeRemoteControlSession(
  sessionId: string,
  token: string
): Promise<void> {
  return remoteFetch(`/api/v1/remote-control/sessions/${sessionId}`, {
    token,
    method: 'DELETE',
  });
}
